/**
 * Per-city scenic enhancements — landmark silhouettes on the cockpit
 * horizon and signature pixel-art props placed on the bird's-eye map.
 *
 *   drawCityHorizon(ctx, cityId, horizonY)
 *     — chunky pixel-art landmark silhouettes drawn into the sky strip
 *       (Eiffel, Big Ben, Pyramids, CN Tower, Empire State, Tokyo Tower,
 *       Templo Mayor, Cedar of Lebanon, RenCen, Hollywood sign, etc.)
 *
 *   drawCityProps(ctx, cityId)
 *     — fixed per-city flavor objects on the world map (palm trees for LA,
 *       neon kanji billboards for Tokyo, red phone boxes for London,
 *       yellow cabs for NYC, papel-picado bunting for Mexico City, etc.)
 *
 * All drawing assumes the caller has already set the world transform and
 * uses raw world coordinates (1280x820).
 */
import { OUTLINE } from "./itemVisuals";

const WORLD_W = 1280;

// --- Palette helpers ---------------------------------------------------------
const BLACK = "#0A0A0A";

// --- Landmark silhouette primitives ------------------------------------------
// Every landmark function draws at a provided baseline Y, centered on X.
// All art is intentionally chunky pixel-art: hard rectangles, no gradients.

function lmEiffel(ctx, x, baseY, h) {
  const w = h * 0.55;
  ctx.fillStyle = "#3a3228";
  // Base — four legs splaying outward
  ctx.beginPath();
  ctx.moveTo(x - w / 2, baseY);
  ctx.lineTo(x - w / 6, baseY - h * 0.30);
  ctx.lineTo(x + w / 6, baseY - h * 0.30);
  ctx.lineTo(x + w / 2, baseY);
  ctx.closePath();
  ctx.fill();
  // Mid-section
  ctx.fillRect(x - w / 6, baseY - h * 0.62, w / 3, h * 0.32);
  // Upper tower
  ctx.fillRect(x - w / 14, baseY - h * 0.88, w / 7, h * 0.26);
  // Antenna
  ctx.fillRect(x - 2, baseY - h, 4, h * 0.12);
  // Lattice highlight lines
  ctx.fillStyle = "rgba(0,0,0,0.35)";
  ctx.fillRect(x - w / 8, baseY - h * 0.60, w / 4, 2);
  ctx.fillRect(x - w / 10, baseY - h * 0.30, w / 5, 2);
}

function lmBigBen(ctx, x, baseY, h) {
  const w = h * 0.22;
  ctx.fillStyle = "#8A704B";
  ctx.fillRect(x - w / 2, baseY - h * 0.95, w, h * 0.95);
  // Clock face
  ctx.fillStyle = "#F1E4C0";
  ctx.fillRect(x - w / 2 + 2, baseY - h * 0.80, w - 4, w - 4);
  ctx.fillStyle = BLACK;
  ctx.fillRect(x - 2, baseY - h * 0.78, 1, w - 10);
  ctx.fillRect(x - 3, baseY - h * 0.78 + (w - 10) / 2, 6, 1);
  // Spire
  ctx.fillStyle = "#5C4628";
  ctx.beginPath();
  ctx.moveTo(x - w / 2, baseY - h * 0.95);
  ctx.lineTo(x, baseY - h);
  ctx.lineTo(x + w / 2, baseY - h * 0.95);
  ctx.closePath();
  ctx.fill();
}

function lmPyramid(ctx, x, baseY, h) {
  const w = h * 1.3;
  ctx.fillStyle = "#C9A868";
  ctx.beginPath();
  ctx.moveTo(x - w / 2, baseY);
  ctx.lineTo(x, baseY - h);
  ctx.lineTo(x + w / 2, baseY);
  ctx.closePath();
  ctx.fill();
  // Shadow side
  ctx.fillStyle = "rgba(0,0,0,0.25)";
  ctx.beginPath();
  ctx.moveTo(x, baseY);
  ctx.lineTo(x + w / 2, baseY);
  ctx.lineTo(x, baseY - h);
  ctx.closePath();
  ctx.fill();
}

function lmCnTower(ctx, x, baseY, h) {
  ctx.fillStyle = "#C8CDD4";
  // Main spike
  ctx.fillRect(x - 3, baseY - h, 6, h);
  // SkyPod observation dish — pixel-art flat oval
  ctx.fillStyle = "#9099A0";
  ctx.fillRect(x - 14, baseY - h * 0.68, 28, 6);
  ctx.fillRect(x - 11, baseY - h * 0.68 - 4, 22, 4);
  ctx.fillRect(x - 11, baseY - h * 0.68 + 6, 22, 3);
  // Top antenna
  ctx.fillStyle = "#6F787E";
  ctx.fillRect(x - 1, baseY - h - 8, 2, 8);
}

function lmEmpireState(ctx, x, baseY, h) {
  const w = h * 0.25;
  ctx.fillStyle = "#6F7B8E";
  ctx.fillRect(x - w / 2, baseY - h * 0.80, w, h * 0.80);
  // Stepped art-deco tiers
  ctx.fillRect(x - w / 3, baseY - h * 0.90, w * 0.66, h * 0.12);
  ctx.fillRect(x - w / 5, baseY - h * 0.96, w * 0.40, h * 0.08);
  // Spire + lightning rod
  ctx.fillRect(x - 2, baseY - h, 4, h * 0.04);
  // Windows hint
  ctx.fillStyle = "#FFD166";
  for (let y = baseY - h * 0.78; y < baseY - h * 0.08; y += 6) {
    ctx.fillRect(x - w / 2 + 2, y, 1, 1);
    ctx.fillRect(x + w / 2 - 3, y, 1, 1);
  }
}

function lmChrysler(ctx, x, baseY, h) {
  const w = h * 0.18;
  ctx.fillStyle = "#8590A6";
  ctx.fillRect(x - w / 2, baseY - h * 0.75, w, h * 0.75);
  // Triangular crown
  ctx.fillStyle = "#C0C8D4";
  ctx.beginPath();
  ctx.moveTo(x - w / 2, baseY - h * 0.75);
  ctx.lineTo(x, baseY - h);
  ctx.lineTo(x + w / 2, baseY - h * 0.75);
  ctx.closePath();
  ctx.fill();
}

function lmTokyoTower(ctx, x, baseY, h) {
  const w = h * 0.35;
  ctx.fillStyle = "#E63946";
  // Four splayed legs
  ctx.beginPath();
  ctx.moveTo(x - w / 2, baseY);
  ctx.lineTo(x - w / 6, baseY - h * 0.45);
  ctx.lineTo(x + w / 6, baseY - h * 0.45);
  ctx.lineTo(x + w / 2, baseY);
  ctx.closePath();
  ctx.fill();
  // Central mast
  ctx.fillRect(x - w / 12, baseY - h * 0.95, w / 6, h * 0.50);
  ctx.fillRect(x - 1, baseY - h, 2, h * 0.06);
  // Observation deck
  ctx.fillStyle = "#FFD166";
  ctx.fillRect(x - w / 7, baseY - h * 0.65, w / 3.5, 4);
}

function lmSkytree(ctx, x, baseY, h) {
  ctx.fillStyle = "#C4D4E0";
  ctx.fillRect(x - 2, baseY - h, 4, h);
  // Deck 1
  ctx.fillRect(x - 6, baseY - h * 0.72, 12, 4);
  // Deck 2
  ctx.fillRect(x - 4, baseY - h * 0.55, 8, 3);
  // Antenna
  ctx.fillStyle = "#8090A0";
  ctx.fillRect(x - 1, baseY - h - 6, 2, 6);
}

function lmHollywoodSign(ctx, x, baseY, h) {
  ctx.fillStyle = "#F5F5F5";
  const letters = "HOLLYWOOD";
  const letterW = h * 0.7 / letters.length;
  for (let i = 0; i < letters.length; i++) {
    ctx.fillRect(x - (letters.length * letterW) / 2 + i * letterW, baseY - h, letterW - 1, h);
  }
}

function lmRenCen(ctx, x, baseY, h) {
  // Four cylindrical towers — center tallest
  ctx.fillStyle = "#4A5A7A";
  const layout = [-10, -5, 0, 5];
  const heights = [0.75, 0.85, 1.0, 0.80];
  for (let i = 0; i < layout.length; i++) {
    const th = h * heights[i];
    ctx.fillRect(x + layout[i] - 3, baseY - th, 6, th);
    // Windows — yellow dots
    ctx.fillStyle = "#FFB347";
    for (let yy = baseY - th + 4; yy < baseY - 4; yy += 6) {
      ctx.fillRect(x + layout[i] - 1, yy, 2, 2);
    }
    ctx.fillStyle = "#4A5A7A";
  }
}

function lmSmokestack(ctx, x, baseY, h) {
  ctx.fillStyle = "#6B5148";
  ctx.fillRect(x - 4, baseY - h, 8, h);
  // Red stripe
  ctx.fillStyle = "#B03A2E";
  ctx.fillRect(x - 4, baseY - h * 0.20, 8, 4);
  // Smoke puffs
  ctx.fillStyle = "rgba(180,180,180,0.7)";
  for (let i = 0; i < 3; i++) {
    ctx.fillRect(x - 6 + i * 2, baseY - h - 8 - i * 6, 8, 4);
  }
}

function lmTempMayor(ctx, x, baseY, h) {
  // Stepped Aztec pyramid — 4 tiers
  ctx.fillStyle = "#A07458";
  const tiers = 4;
  const w = h * 1.4;
  for (let i = 0; i < tiers; i++) {
    const tw = w * (1 - i / (tiers + 0.8));
    const ty = baseY - (h / tiers) * (i + 1);
    ctx.fillRect(x - tw / 2, ty, tw, h / tiers);
  }
  // Shrine at top
  ctx.fillStyle = "#E63946";
  ctx.fillRect(x - 4, baseY - h - 4, 8, 4);
}

function lmVolcano(ctx, x, baseY, h) {
  const w = h * 1.3;
  ctx.fillStyle = "#6B5A80";
  ctx.beginPath();
  ctx.moveTo(x - w / 2, baseY);
  ctx.lineTo(x - w * 0.15, baseY - h * 0.85);
  ctx.lineTo(x - w * 0.05, baseY - h * 0.90);
  ctx.lineTo(x + w * 0.05, baseY - h);
  ctx.lineTo(x + w * 0.20, baseY - h * 0.82);
  ctx.lineTo(x + w / 2, baseY);
  ctx.closePath();
  ctx.fill();
  // Snow cap
  ctx.fillStyle = "#F5F5F5";
  ctx.beginPath();
  ctx.moveTo(x - w * 0.15, baseY - h * 0.85);
  ctx.lineTo(x - w * 0.05, baseY - h * 0.90);
  ctx.lineTo(x + w * 0.05, baseY - h);
  ctx.lineTo(x + w * 0.20, baseY - h * 0.82);
  ctx.lineTo(x + w * 0.08, baseY - h * 0.74);
  ctx.lineTo(x - w * 0.08, baseY - h * 0.78);
  ctx.closePath();
  ctx.fill();
}

function lmCedar(ctx, x, baseY, h) {
  const w = h * 0.9;
  // Trunk
  ctx.fillStyle = "#5C3A1E";
  ctx.fillRect(x - 2, baseY - h * 0.20, 4, h * 0.20);
  // Tiered horizontal canopy (cedar of Lebanon — wide flat tiers)
  ctx.fillStyle = "#1F5A2D";
  for (let i = 0; i < 3; i++) {
    const ty = baseY - h * 0.28 - i * h * 0.22;
    const tw = w - i * (w * 0.20);
    ctx.fillRect(x - tw / 2, ty, tw, h * 0.16);
  }
}

function lmPigeonRocks(ctx, x, baseY, h) {
  ctx.fillStyle = "#8E7B5C";
  // Two stacks
  ctx.fillRect(x - 14, baseY - h * 0.9, 10, h * 0.9);
  ctx.fillRect(x + 4, baseY - h * 0.7, 8, h * 0.7);
  // Water base
  ctx.fillStyle = "rgba(70, 130, 180, 0.55)";
  ctx.fillRect(x - 20, baseY - 4, 40, 4);
}

function lmMinaret(ctx, x, baseY, h) {
  ctx.fillStyle = "#D4B584";
  ctx.fillRect(x - 2, baseY - h * 0.85, 4, h * 0.85);
  // Dome
  ctx.beginPath();
  ctx.arc(x, baseY - h * 0.85, 4, 0, Math.PI * 2);
  ctx.fill();
  // Crescent
  ctx.fillRect(x - 1, baseY - h, 2, h * 0.08);
}

function lmRogersDome(ctx, x, baseY, h) {
  ctx.fillStyle = "#B0B8C4";
  ctx.beginPath();
  ctx.arc(x, baseY, h, Math.PI, 0);
  ctx.fill();
  // Ribbed panels
  ctx.strokeStyle = "rgba(0,0,0,0.3)";
  ctx.lineWidth = 1;
  for (let i = -2; i <= 2; i++) {
    ctx.beginPath();
    ctx.moveTo(x + i * (h * 0.35), baseY);
    ctx.lineTo(x + i * (h * 0.2), baseY - h);
    ctx.stroke();
  }
}

function lmFisherTower(ctx, x, baseY, h) {
  ctx.fillStyle = "#704E3A";
  ctx.fillRect(x - 4, baseY - h * 0.85, 8, h * 0.85);
  // Pyramid gold roof
  ctx.fillStyle = "#E6B422";
  ctx.beginPath();
  ctx.moveTo(x - 5, baseY - h * 0.85);
  ctx.lineTo(x, baseY - h);
  ctx.lineTo(x + 5, baseY - h * 0.85);
  ctx.closePath();
  ctx.fill();
}

function lmDome(ctx, x, baseY, h) {
  // Generic white dome (Sacré-Cœur / capitol)
  ctx.fillStyle = "#F0F0F0";
  ctx.beginPath();
  ctx.arc(x, baseY - h * 0.3, h * 0.4, Math.PI, 0);
  ctx.fill();
  ctx.fillRect(x - h * 0.4, baseY - h * 0.3, h * 0.8, h * 0.3);
  // Cross on top
  ctx.fillStyle = BLACK;
  ctx.fillRect(x - 1, baseY - h - 2, 2, 6);
}

function lmTorreLatin(ctx, x, baseY, h) {
  ctx.fillStyle = "#9AA0B0";
  ctx.fillRect(x - 3, baseY - h, 6, h);
  // Antenna
  ctx.fillRect(x - 1, baseY - h - 6, 2, 6);
  // Windows
  ctx.fillStyle = "#FFD166";
  for (let yy = baseY - h + 4; yy < baseY - 4; yy += 4) {
    ctx.fillRect(x - 2, yy, 4, 1);
  }
}

// --- Registry of landmark drawer funcs by id --------------------------------
const LM = {
  eiffel: lmEiffel, bigben: lmBigBen, pyramid: lmPyramid, cntower: lmCnTower,
  empire: lmEmpireState, chrysler: lmChrysler, tokyotower: lmTokyoTower,
  skytree: lmSkytree, hollywood: lmHollywoodSign, rencen: lmRenCen,
  smokestack: lmSmokestack, temploMayor: lmTempMayor, volcano: lmVolcano,
  cedar: lmCedar, pigeonRocks: lmPigeonRocks, minaret: lmMinaret,
  rogersDome: lmRogersDome, fisherTower: lmFisherTower, dome: lmDome,
  torreLatin: lmTorreLatin,
};

// --- Per-city scene configs --------------------------------------------------
// Each city declares:
//   horizon: list of { id, xFrac, h } landmark silhouettes to place on the
//            cockpit horizon. xFrac is 0..1 across WORLD_W.
//   props:   list of { type, x, y } ground props placed on the bird's-eye map.
//            Props avoid the road cross (WORLD_W/2 ± ~60, WORLD_H/2 ± ~60).
//   groundTint: optional overlay color (applied at very low opacity on bird's-eye)
//               to subtly shift the mood (sand for Cairo, cold-blue for Toronto).
const CITY_SCENES = {
  paris: {
    horizon: [
      { id: "eiffel", xFrac: 0.50, h: 150 },
      { id: "dome", xFrac: 0.82, h: 50 },
    ],
    props: [
      { type: "cafe", x: 230, y: 180 },
      { type: "lamppost", x: 410, y: 620 },
      { type: "lamppost", x: 900, y: 200 },
    ],
  },
  nyc: {
    horizon: [
      { id: "empire", xFrac: 0.52, h: 170 },
      { id: "chrysler", xFrac: 0.68, h: 140 },
      { id: "empire", xFrac: 0.18, h: 140 },
    ],
    props: [
      { type: "yellowcab", x: 230, y: 170 },
      { type: "yellowcab", x: 1050, y: 700 },
      { type: "billboard", x: 430, y: 200 },
      { type: "hotdogcart", x: 880, y: 620 },
    ],
  },
  la: {
    horizon: [
      { id: "hollywood", xFrac: 0.18, h: 32 },
      { id: "torreLatin", xFrac: 0.75, h: 130 },
    ],
    props: [
      { type: "palm", x: 150, y: 150 },
      { type: "palm", x: 1080, y: 200 },
      { type: "palm", x: 180, y: 700 },
      { type: "palm", x: 1100, y: 700 },
    ],
  },
  mexico_city: {
    horizon: [
      { id: "temploMayor", xFrac: 0.28, h: 80 },
      { id: "torreLatin", xFrac: 0.62, h: 130 },
      { id: "volcano", xFrac: 0.90, h: 110 },
    ],
    props: [
      { type: "papel", x: 240, y: 180 },
      { type: "papel", x: 1030, y: 650 },
      { type: "cactus", x: 160, y: 650 },
      { type: "cactus", x: 1080, y: 210 },
    ],
  },
  london: {
    horizon: [
      { id: "bigben", xFrac: 0.35, h: 140 },
      { id: "dome", xFrac: 0.65, h: 50 },
    ],
    props: [
      { type: "phonebox", x: 180, y: 180 },
      { type: "phonebox", x: 1050, y: 680 },
      { type: "doubledecker", x: 900, y: 170 },
    ],
  },
  beirut: {
    horizon: [
      { id: "cedar", xFrac: 0.18, h: 60 },
      { id: "pigeonRocks", xFrac: 0.84, h: 70 },
    ],
    props: [
      { type: "palm", x: 200, y: 680 },
      { type: "cedar", x: 1050, y: 170 },
      { type: "cedar", x: 180, y: 180 },
    ],
  },
  cairo: {
    horizon: [
      { id: "pyramid", xFrac: 0.74, h: 110 },
      { id: "pyramid", xFrac: 0.82, h: 85 },
      { id: "pyramid", xFrac: 0.89, h: 62 },
      { id: "minaret", xFrac: 0.25, h: 140 },
    ],
    props: [
      { type: "palm", x: 160, y: 680 },
      { type: "palm", x: 1090, y: 180 },
      { type: "camel", x: 900, y: 650 },
    ],
  },
  toronto: {
    horizon: [
      { id: "cntower", xFrac: 0.52, h: 200 },
      { id: "rogersDome", xFrac: 0.41, h: 36 },
    ],
    props: [
      { type: "snowman", x: 220, y: 180 },
      { type: "snowman", x: 1050, y: 700 },
      { type: "mapletree", x: 180, y: 680 },
      { type: "mapletree", x: 1080, y: 190 },
    ],
  },
  tokyo: {
    horizon: [
      { id: "tokyotower", xFrac: 0.56, h: 170 },
      { id: "skytree", xFrac: 0.80, h: 190 },
    ],
    props: [
      { type: "neon", x: 210, y: 190, text: "カラオケ", color: "#FF3DF8" },
      { type: "neon", x: 1030, y: 650, text: "居酒屋", color: "#00F0FF" },
      { type: "torii", x: 180, y: 670 },
    ],
  },
  detroit: {
    horizon: [
      { id: "rencen", xFrac: 0.56, h: 150 },
      { id: "fisherTower", xFrac: 0.82, h: 120 },
      { id: "smokestack", xFrac: 0.15, h: 110 },
      { id: "smokestack", xFrac: 0.22, h: 90 },
    ],
    props: [
      { type: "factorySmokestack", x: 200, y: 180 },
      { type: "factorySmokestack", x: 1050, y: 690 },
      { type: "rustedcar", x: 880, y: 180 },
    ],
  },
};

export function drawCityHorizon(ctx, cityId, horizonY) {
  const cfg = CITY_SCENES[cityId];
  if (!cfg || !cfg.horizon) return;
  for (const lm of cfg.horizon) {
    const drawer = LM[lm.id];
    if (!drawer) continue;
    drawer(ctx, Math.round(lm.xFrac * WORLD_W), horizonY, lm.h);
  }
}

// --- Ground prop drawers (bird's-eye) ---------------------------------------

function propPalm(ctx, x, y) {
  // Trunk
  ctx.fillStyle = "#5C3A1E";
  ctx.fillRect(x - 2, y - 4, 4, 14);
  // Ring highlights
  ctx.fillStyle = "#3A2512";
  ctx.fillRect(x - 2, y + 1, 4, 1);
  ctx.fillRect(x - 2, y + 5, 4, 1);
  // Fronds (top-down view — star pattern)
  ctx.fillStyle = "#2E9D5E";
  for (const a of [0, Math.PI / 3, (2 * Math.PI) / 3, Math.PI, (4 * Math.PI) / 3, (5 * Math.PI) / 3]) {
    ctx.save();
    ctx.translate(x, y - 4);
    ctx.rotate(a);
    ctx.fillRect(0, -2, 14, 4);
    ctx.restore();
  }
  // Coconuts
  ctx.fillStyle = "#5C3A1E";
  ctx.fillRect(x - 3, y - 5, 2, 2);
  ctx.fillRect(x + 1, y - 5, 2, 2);
}

function propLamppost(ctx, x, y) {
  ctx.fillStyle = "#2C2C2C";
  ctx.fillRect(x - 1, y - 16, 2, 16);
  // Shade
  ctx.fillStyle = "#FFD166";
  ctx.fillRect(x - 4, y - 20, 8, 4);
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 1;
  ctx.strokeRect(x - 4, y - 20, 8, 4);
  // Glow
  ctx.fillStyle = "rgba(255, 209, 102, 0.25)";
  ctx.beginPath();
  ctx.arc(x, y - 18, 18, 0, Math.PI * 2);
  ctx.fill();
}

function propCafe(ctx, x, y) {
  // Parisian café table + 2 chairs + red awning
  ctx.fillStyle = "#E63946";
  ctx.fillRect(x - 12, y - 10, 24, 4);
  // Stripes on awning
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(x - 10, y - 10, 3, 4);
  ctx.fillRect(x - 4, y - 10, 3, 4);
  ctx.fillRect(x + 2, y - 10, 3, 4);
  ctx.fillRect(x + 8, y - 10, 3, 4);
  // Round table
  ctx.fillStyle = "#7B5E3D";
  ctx.beginPath();
  ctx.arc(x, y, 5, 0, Math.PI * 2);
  ctx.fill();
  // Chairs (small squares)
  ctx.fillRect(x - 10, y - 2, 4, 4);
  ctx.fillRect(x + 6, y - 2, 4, 4);
}

function propYellowCab(ctx, x, y) {
  ctx.fillStyle = "#FFCC00";
  ctx.fillRect(x - 10, y - 6, 20, 12);
  // Roof sign
  ctx.fillStyle = "#000";
  ctx.fillRect(x - 4, y - 9, 8, 3);
  ctx.fillStyle = "#FFCC00";
  ctx.fillRect(x - 3, y - 8, 6, 1);
  // Windows
  ctx.fillStyle = "#4A6B8A";
  ctx.fillRect(x - 8, y - 4, 16, 4);
  // Checker stripe
  ctx.fillStyle = "#000";
  for (let i = 0; i < 8; i++) ctx.fillRect(x - 10 + i * 3, y + 1, 1, 2);
  // Wheels
  ctx.fillStyle = "#1a1a1a";
  ctx.fillRect(x - 9, y + 5, 3, 3);
  ctx.fillRect(x + 6, y + 5, 3, 3);
}

function propBillboard(ctx, x, y) {
  // Giant advertising billboard — Times Square style
  ctx.fillStyle = "#111";
  ctx.fillRect(x - 18, y - 18, 36, 16);
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 1;
  ctx.strokeRect(x - 18, y - 18, 36, 16);
  // Flashing multicolor content
  const t = Math.floor(performance.now() / 220) % 4;
  const colors = ["#E63946", "#FFCC00", "#34C759", "#1E90FF"];
  ctx.fillStyle = colors[t];
  ctx.fillRect(x - 14, y - 14, 28, 8);
  // Support post
  ctx.fillStyle = "#444";
  ctx.fillRect(x - 1, y - 2, 2, 6);
}

function propHotDogCart(ctx, x, y) {
  // Red cart with umbrella
  ctx.fillStyle = "#E63946";
  ctx.fillRect(x - 8, y - 4, 16, 8);
  // White stripe
  ctx.fillStyle = "#FFF";
  ctx.fillRect(x - 8, y - 1, 16, 2);
  // Yellow umbrella
  ctx.fillStyle = "#FFCC00";
  ctx.beginPath();
  ctx.moveTo(x - 10, y - 10);
  ctx.lineTo(x, y - 18);
  ctx.lineTo(x + 10, y - 10);
  ctx.closePath();
  ctx.fill();
  // Pole
  ctx.fillStyle = "#333";
  ctx.fillRect(x - 1, y - 10, 2, 10);
  // Wheels
  ctx.fillStyle = "#1a1a1a";
  ctx.fillRect(x - 8, y + 3, 3, 3);
  ctx.fillRect(x + 5, y + 3, 3, 3);
}

function propPapel(ctx, x, y) {
  // Papel picado — colorful triangle bunting strung between two points
  const colors = ["#E63946", "#FFCC00", "#34C759", "#FF1493", "#1E90FF"];
  ctx.strokeStyle = "#8B4513";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x - 24, y - 10);
  ctx.bezierCurveTo(x - 8, y - 5, x + 8, y - 5, x + 24, y - 10);
  ctx.stroke();
  for (let i = 0; i < 8; i++) {
    const tx = x - 24 + i * 7;
    const ty = y - 8 + Math.abs(i - 3.5) * -0.6;
    ctx.fillStyle = colors[i % colors.length];
    ctx.beginPath();
    ctx.moveTo(tx, ty);
    ctx.lineTo(tx - 2, ty + 5);
    ctx.lineTo(tx + 2, ty + 5);
    ctx.closePath();
    ctx.fill();
  }
}

function propCactus(ctx, x, y) {
  ctx.fillStyle = "#2E9D5E";
  // Main stem
  ctx.fillRect(x - 2, y - 12, 4, 16);
  // Left arm
  ctx.fillRect(x - 6, y - 8, 2, 6);
  ctx.fillRect(x - 6, y - 10, 4, 2);
  // Right arm
  ctx.fillRect(x + 4, y - 6, 2, 5);
  ctx.fillRect(x + 4, y - 8, 4, 2);
  // Spines
  ctx.fillStyle = "#F5F5F5";
  for (let i = 0; i < 3; i++) ctx.fillRect(x - 1, y - 10 + i * 4, 1, 1);
}

function propPhoneBox(ctx, x, y) {
  ctx.fillStyle = "#C8102E";
  ctx.fillRect(x - 4, y - 12, 8, 12);
  // Window panels
  ctx.fillStyle = "#2A2A2A";
  ctx.fillRect(x - 3, y - 10, 6, 6);
  // Top "TELEPHONE" sign
  ctx.fillStyle = "#FFF";
  ctx.fillRect(x - 4, y - 14, 8, 2);
  // Crown
  ctx.fillStyle = "#8B0A1B";
  ctx.fillRect(x - 3, y - 16, 6, 2);
}

function propDoubleDecker(ctx, x, y) {
  ctx.fillStyle = "#C8102E";
  ctx.fillRect(x - 14, y - 10, 28, 18);
  // Windows
  ctx.fillStyle = "#4A6B8A";
  ctx.fillRect(x - 12, y - 8, 24, 4);
  ctx.fillRect(x - 12, y - 2, 24, 4);
  // Stripe
  ctx.fillStyle = "#FFF";
  ctx.fillRect(x - 14, y - 4, 28, 2);
  // Wheels
  ctx.fillStyle = "#1a1a1a";
  ctx.fillRect(x - 12, y + 7, 4, 3);
  ctx.fillRect(x + 8, y + 7, 4, 3);
}

function propCedar(ctx, x, y) {
  // Trunk
  ctx.fillStyle = "#5C3A1E";
  ctx.fillRect(x - 1, y - 2, 2, 6);
  // Wide flat tiers (cedar of Lebanon distinct shape)
  ctx.fillStyle = "#1F5A2D";
  ctx.fillRect(x - 10, y - 4, 20, 3);
  ctx.fillRect(x - 8, y - 7, 16, 3);
  ctx.fillRect(x - 6, y - 10, 12, 3);
  // Light highlights
  ctx.fillStyle = "#2E9D5E";
  ctx.fillRect(x - 8, y - 4, 2, 1);
  ctx.fillRect(x - 6, y - 7, 2, 1);
}

function propCamel(ctx, x, y) {
  ctx.fillStyle = "#C9A868";
  // Body
  ctx.fillRect(x - 8, y - 4, 16, 6);
  // Two humps
  ctx.fillRect(x - 5, y - 6, 4, 2);
  ctx.fillRect(x + 1, y - 6, 4, 2);
  // Neck
  ctx.fillRect(x + 7, y - 7, 3, 3);
  // Head
  ctx.fillRect(x + 9, y - 9, 3, 3);
  // Legs
  ctx.fillRect(x - 6, y + 2, 2, 4);
  ctx.fillRect(x + 4, y + 2, 2, 4);
}

function propSnowman(ctx, x, y) {
  ctx.fillStyle = "#FFFFFF";
  // Body
  ctx.beginPath();
  ctx.arc(x, y + 1, 6, 0, Math.PI * 2);
  ctx.fill();
  // Head
  ctx.beginPath();
  ctx.arc(x, y - 8, 4, 0, Math.PI * 2);
  ctx.fill();
  // Eyes
  ctx.fillStyle = BLACK;
  ctx.fillRect(x - 2, y - 9, 1, 1);
  ctx.fillRect(x + 1, y - 9, 1, 1);
  // Carrot
  ctx.fillStyle = "#FF6B35";
  ctx.fillRect(x, y - 8, 2, 1);
  // Buttons
  ctx.fillRect(x - 1, y - 1, 1, 1);
  ctx.fillRect(x - 1, y + 2, 1, 1);
}

function propMapleTree(ctx, x, y) {
  // Trunk
  ctx.fillStyle = "#5C3A1E";
  ctx.fillRect(x - 2, y - 2, 4, 8);
  // Canopy — fiery orange/red maple
  const colors = ["#E63946", "#FF6B35", "#FFCC00"];
  ctx.fillStyle = colors[Math.floor(Math.abs(x + y) / 30) % 3];
  ctx.beginPath();
  ctx.arc(x, y - 6, 8, 0, Math.PI * 2);
  ctx.fill();
  // Highlight
  ctx.fillStyle = "#FFF8D6";
  ctx.fillRect(x - 4, y - 8, 2, 2);
}

function propNeon(ctx, x, y, text = "NEON", color = "#FF3DF8") {
  // Vertical neon sign — kanji characters stacked
  ctx.fillStyle = "#111";
  ctx.fillRect(x - 6, y - 18, 12, 20);
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.strokeRect(x - 6, y - 18, 12, 20);
  // Glowing text — pulse by time
  const pulse = 0.7 + 0.3 * Math.sin(performance.now() / 300);
  ctx.save();
  ctx.globalAlpha = pulse;
  ctx.fillStyle = color;
  ctx.font = "bold 8px monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  for (let i = 0; i < text.length; i++) {
    ctx.fillText(text[i], x, y - 15 + i * 5);
  }
  ctx.restore();
  // Support pole
  ctx.fillStyle = "#444";
  ctx.fillRect(x - 1, y + 2, 2, 4);
}

function propTorii(ctx, x, y) {
  ctx.fillStyle = "#C8102E";
  // Two vertical pillars
  ctx.fillRect(x - 10, y - 14, 3, 16);
  ctx.fillRect(x + 7, y - 14, 3, 16);
  // Top lintel
  ctx.fillRect(x - 13, y - 14, 26, 3);
  // Upper curved bar
  ctx.fillRect(x - 14, y - 18, 28, 3);
}

function propFactorySmokestack(ctx, x, y) {
  // Industrial stack + building
  ctx.fillStyle = "#6B5148";
  ctx.fillRect(x - 3, y - 18, 6, 18);
  // Smoke
  ctx.fillStyle = "rgba(180,180,180,0.6)";
  for (let i = 0; i < 3; i++) ctx.fillRect(x - 6 + i * 2, y - 24 - i * 4, 6, 3);
  // Factory block base
  ctx.fillStyle = "#5A4636";
  ctx.fillRect(x - 10, y, 20, 4);
}

function propRustedCar(ctx, x, y) {
  ctx.fillStyle = "#7E3F1F";
  ctx.fillRect(x - 9, y - 4, 18, 8);
  // Rust patches
  ctx.fillStyle = "#4A2410";
  ctx.fillRect(x - 7, y - 3, 3, 2);
  ctx.fillRect(x + 4, y - 2, 3, 3);
  // Broken windshield
  ctx.fillStyle = "#3A4A5A";
  ctx.fillRect(x - 7, y - 3, 14, 2);
  // Missing wheels (no black dots)
}

const PROP_FNS = {
  palm: propPalm, lamppost: propLamppost, cafe: propCafe,
  yellowcab: propYellowCab, billboard: propBillboard, hotdogcart: propHotDogCart,
  papel: propPapel, cactus: propCactus, phonebox: propPhoneBox,
  doubledecker: propDoubleDecker, cedar: propCedar, camel: propCamel,
  snowman: propSnowman, mapletree: propMapleTree, neon: propNeon,
  torii: propTorii, factorySmokestack: propFactorySmokestack,
  rustedcar: propRustedCar,
};

export function drawCityProps(ctx, cityId) {
  const cfg = CITY_SCENES[cityId];
  if (!cfg || !cfg.props) return;
  for (const p of cfg.props) {
    const fn = PROP_FNS[p.type];
    if (!fn) continue;
    if (p.type === "neon") fn(ctx, p.x, p.y, p.text, p.color);
    else fn(ctx, p.x, p.y);
  }
}

// --- Bird's-eye road-lined buildings ----------------------------------------
// Paint a short row of city-flavored building blocks along each side of
// the main road cross (x = WORLD_W/2 ± 48..200 and y = WORLD_H/2 ± 48..200).
// Each block uses the per-city building palette. Called AFTER roads but
// BEFORE props so props can sit on the sidewalks.
const WORLD_H = 820;
const CITY_BUILDING_PALETTES = {
  paris: { walls: ["#E8D5B0", "#D2BA8B", "#BFA372"], roofs: "#3E4449", windows: "#FFE499" },
  london: { walls: ["#A64E4E", "#8B3535", "#6F2828"], roofs: "#2F2F2F", windows: "#F5E3B0" },
  tokyo: { walls: ["#1A1F3A", "#252B4D", "#3D2A5C"], roofs: "#1A1A1A", windows: "#FF3DF8" },
  nyc: { walls: ["#8B7355", "#A9896F", "#6B5E4B"], roofs: "#2F2F2F", windows: "#FFD700" },
  la: { walls: ["#F5D5A8", "#E8B580", "#D99066"], roofs: "#7A4A2F", windows: "#FFFFFF" },
  mexico_city: { walls: ["#F2A0C8", "#FFD23F", "#7ED6A5"], roofs: "#8B4513", windows: "#FFFFFF" },
  cairo: { walls: ["#E8C987", "#C9A86B", "#D4B77A"], roofs: "#A0845A", windows: "#FFD580" },
  toronto: { walls: ["#C9D1D9", "#8DA5B8", "#5A7A95"], roofs: "#2F3F4F", windows: "#FFFFFF" },
  beirut: { walls: ["#F5E8D0", "#E8D5B5", "#D4C195"], roofs: "#4A90C8", windows: "#5E9FE8" },
  detroit: { walls: ["#8B4513", "#5D2F0A", "#B87333"], roofs: "#2F2F2F", windows: "#FFB347" },
};

export function drawCityGroundBuildings(ctx, cityId) {
  const pal = CITY_BUILDING_PALETTES[cityId];
  if (!pal) return;
  const cx = WORLD_W / 2, cy = WORLD_H / 2;
  // Horizontal corridor — buildings lining the top & bottom sidewalks
  _buildingRow(ctx, pal, 80, cy - 96, WORLD_W - 160, 44, "horizontal");
  _buildingRow(ctx, pal, 80, cy + 52, WORLD_W - 160, 44, "horizontal");
  // Vertical corridor — buildings lining left & right sidewalks
  _buildingRow(ctx, pal, cx - 96, 60, 44, WORLD_H - 120, "vertical");
  _buildingRow(ctx, pal, cx + 52, 60, 44, WORLD_H - 120, "vertical");
}

function _buildingRow(ctx, pal, x, y, w, h, orient) {
  // Gap in the middle where the perpendicular road crosses — skip buildings
  // whose bounding-box crosses the main cross. Plus inserts a visible
  // sidewalk gap every 3 buildings so players can clearly see passages
  // through which they can drive out onto the grass for off-road artifacts.
  const cx = WORLD_W / 2, cy = WORLD_H / 2;
  let cursor = orient === "horizontal" ? x : y;
  const end = orient === "horizontal" ? x + w : y + h;
  let i = 0;
  while (cursor < end) {
    // Every 3rd building skip → 18-pixel passage. Players can drive through
    // these to access the grass beyond.
    if (i > 0 && i % 3 === 0) {
      cursor += 18;
      i += 1;
      continue;
    }
    const seed = (cursor * 137 + (orient === "horizontal" ? 7 : 23)) % 509;
    const bsize = 36 + (seed % 32); // per-building length along the road
    const b = {};
    if (orient === "horizontal") {
      b.x = cursor; b.y = y; b.w = bsize; b.h = h;
    } else {
      b.x = x; b.y = cursor; b.w = w; b.h = bsize;
    }
    // Skip if crosses the road gap
    const crossX = orient === "horizontal" && (b.x + b.w > cx - 56 && b.x < cx + 56);
    const crossY = orient === "vertical" && (b.y + b.h > cy - 56 && b.y < cy + 56);
    if (!crossX && !crossY) {
      // Wall
      const wallColor = pal.walls[seed % pal.walls.length];
      ctx.fillStyle = wallColor;
      ctx.fillRect(b.x, b.y, b.w, b.h);
      // Outline
      ctx.strokeStyle = OUTLINE;
      ctx.lineWidth = 1;
      ctx.strokeRect(b.x, b.y, b.w, b.h);
      // Roof stripe (top edge)
      ctx.fillStyle = pal.roofs;
      if (orient === "horizontal") {
        ctx.fillRect(b.x, b.y, b.w, 4);
      } else {
        ctx.fillRect(b.x, b.y, 4, b.h);
      }
      // Window dots
      ctx.fillStyle = pal.windows;
      const cols = Math.max(2, Math.floor(b.w / 8));
      const rows = Math.max(2, Math.floor(b.h / 8));
      for (let c = 0; c < cols; c++) {
        for (let r = 0; r < rows; r++) {
          if ((c * 3 + r * 5 + seed) % 3 === 0) continue;
          const wx = b.x + 3 + c * ((b.w - 6) / Math.max(1, cols - 1)) - 1;
          const wy = b.y + 3 + r * ((b.h - 6) / Math.max(1, rows - 1)) - 1;
          ctx.fillRect(wx, wy, 2, 2);
        }
      }
    }
    cursor += bsize + 2;
    i++;
    if (i > 40) break; // safety
  }
}

export function isPropPosition(cityId, x, y, radius = 24) {
  const cfg = CITY_SCENES[cityId];
  if (!cfg || !cfg.props) return false;
  return cfg.props.some((p) => Math.hypot(p.x - x, p.y - y) < radius);
}

// Total signature props for a city (used for "Tourist X/Y" HUD + badge progress).
export function getPropCount(cityId) {
  const cfg = CITY_SCENES[cityId];
  return cfg && cfg.props ? cfg.props.length : 0;
}

// Return the index of the FIRST prop within `radius` of (x,y), or -1. Used to
// mark distinct visited indices in a Set so a player can't re-visit the same
// prop to pad their tourism count.
export function findNearbyPropIndex(cityId, x, y, radius = 36) {
  const cfg = CITY_SCENES[cityId];
  if (!cfg || !cfg.props) return -1;
  for (let i = 0; i < cfg.props.length; i++) {
    const p = cfg.props[i];
    if (Math.hypot(p.x - x, p.y - y) < radius) return i;
  }
  return -1;
}
